import {
  Body1Strong,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  DialogTitle,
  DialogTrigger,
  Link as FluentLink,
  Menu,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, DeleteRegular, DismissRegular, MoreHorizontalFilled } from '@fluentui/react-icons';
import { EHandlePdfBlobMode, handlePdfBlob } from '@modules/items';
import { Avatar, useUsers } from '@modules/users';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import { useSelectableTable } from '@shared/hooks';
import { ButtonLoading, DateLabel, MenuItemLoading, TableActionButtons, UploadButton } from '@shared/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddAttachments, useRemoveAttachmentFromDraft, useRemoveAttachmentsFromDraft } from '../../api';
import { TDocumentAttachment } from '../../types';

type TAttachmentEntry = {
  attachmentId: string;
  attachment?: TDocumentAttachment;
};

type TMode = 'edit' | 'readonly-version' | 'readonly-permissions' | 'readonly-no-draft';

type TProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  token: string;
  entries: TAttachmentEntry[];
  mode: TMode;
};

const useStyles = makeStyles({
  surface: {
    maxWidth: '720px',
    width: 'min(720px, calc(100vw - 32px))',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
  },
  selectedCount: {
    color: 'var(--colorNeutralForeground2)',
  },
  tableWrap: {
    maxHeight: '360px',
    overflowY: 'auto',
  },
  nameCell: {
    minWidth: 0,
    width: '100%',
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    minWidth: 0,
  },
  name: {
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  nameLink: {
    display: 'block',
    minWidth: 0,
    width: '100%',
    overflowWrap: 'anywhere',
  },
  missingName: {
    color: 'var(--colorNeutralForeground3)',
  },
  creatorCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
  emptyState: {
    padding: '12px 0',
  },
});

const MODE_MESSAGE: Record<Exclude<TMode, 'edit'>, string> = {
  'readonly-version': 'Attachments are read-only while viewing a saved version.',
  'readonly-permissions': "You can view attachments here, but you don't have permission to manage them.",
  'readonly-no-draft': 'Create or open a draft to manage attachments.',
};

const DocumentAttachmentsDialog = ({ open, setOpen, documentId, token, entries, mode }: TProps) => {
  const classes = useStyles();
  const tableClasses = useItemTableStyles();
  const { userMap } = useUsers();
  const addAttachments = useAddAttachments();
  const removeAttachment = useRemoveAttachmentFromDraft();
  const removeAttachments = useRemoveAttachmentsFromDraft();
  const canManageAttachments = mode === 'edit';
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());

  const handleOpenAttachment = useCallback(
    (attachment: TDocumentAttachment) => {
      handlePdfBlob(documentId, attachment.blobName, token, EHandlePdfBlobMode.VIEW, attachment.fileName);
    },
    [documentId, token]
  );
  const handleExportAttachment = useCallback(
    (attachment: TDocumentAttachment) => {
      handlePdfBlob(documentId, attachment.blobName, token, EHandlePdfBlobMode.DOWNLOAD, attachment.fileName);
    },
    [documentId, token]
  );

  const handleUpload = (files: File[]) => {
    if (!files.length) return;
    addAttachments.mutate({ id: documentId, files });
  };

  useEffect(() => {
    if (!open) {
      setSelectedAttachmentIds(new Set());
      return;
    }

    setSelectedAttachmentIds((prev) => {
      const next = new Set<string>();
      const validIds = new Set(entries.map((entry) => entry.attachmentId));
      prev.forEach((attachmentId) => {
        if (validIds.has(attachmentId)) next.add(attachmentId);
      });
      return next;
    });
  }, [entries, open]);

  const { selectionHeaderCell, tableRows } = useSelectableTable(
    ['name', 'createdDate', 'creator', 'actions'],
    entries,
    (entry) => entry.attachmentId,
    selectedAttachmentIds,
    setSelectedAttachmentIds
  );

  const selectedEntries = useMemo(
    () => entries.filter((entry) => selectedAttachmentIds.has(entry.attachmentId)),
    [entries, selectedAttachmentIds]
  );
  const isBulkRemoving = removeAttachments.isPending;

  const handleRemoveSelected = useCallback(async () => {
    if (!canManageAttachments || selectedEntries.length === 0) return;

    await removeAttachments.mutateAsync({
      id: documentId,
      attachmentIds: selectedEntries.map(({ attachmentId }) => attachmentId),
    });
    setSelectedAttachmentIds(new Set());
  }, [canManageAttachments, documentId, removeAttachments, selectedEntries]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={classes.surface} aria-describedby={undefined}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close attachments dialog" />
              </DialogTrigger>
            }
          >
            Attachments
          </DialogTitle>
          <DialogContent className={classes.content}>
            {mode !== 'edit' && <Text>{MODE_MESSAGE[mode]}</Text>}

            <div className={classes.toolbar}>
              <div className={classes.toolbarActions}>
                {canManageAttachments && (
                  <UploadButton
                    Button={ButtonLoading}
                    handleFiles={handleUpload}
                    accept=".pdf,application/pdf"
                    multiple
                    loading={addAttachments.isPending}
                  >
                    Upload PDFs
                  </UploadButton>
                )}
                {selectedAttachmentIds.size > 0 && canManageAttachments && (
                  <ButtonLoading
                    appearance="subtle"
                    icon={isBulkRemoving ? <Spinner size="tiny" /> : <DeleteRegular />}
                    loading={isBulkRemoving}
                    onClick={handleRemoveSelected}
                  >
                    Remove
                  </ButtonLoading>
                )}
              </div>
              <Text className={classes.selectedCount}>
                {selectedAttachmentIds.size > 0
                  ? `${selectedAttachmentIds.size} selected`
                  : `${entries.length} attachment${entries.length === 1 ? '' : 's'}`}
              </Text>
            </div>

            {entries.length === 0 ? (
              <Text className={classes.emptyState}>No attachments for this view.</Text>
            ) : (
              <div className={classes.tableWrap}>
                <Card className={tableClasses.card}>
                  <Table className={tableClasses.table}>
                    <TableHeader>
                      <TableRow>
                        {selectionHeaderCell}
                        <TableHeaderCell style={{ width: '100%' }}>
                          <Body1Strong>Name</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '140px' }}>
                          <Body1Strong>Created</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '80px', textAlign: 'center' }}>
                          <Body1Strong>Creator</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell style={{ width: '56px' }} />
                      </TableRow>
                    </TableHeader>
                    <TableBody className={tableClasses.tableBody}>
                      {tableRows.map((row) => {
                        const { key, item, selectionCell, preventSelect, alwaysVisible, menu, ...tableRowProps } = row;
                        const attachment = item.attachment;
                        const creator = attachment ? userMap?.[attachment.creator] : undefined;
                        const removeLoading =
                          removeAttachment.isPending && removeAttachment.variables?.attachmentId === item.attachmentId;

                        return (
                          <TableRow key={key} {...tableRowProps} onContextMenu={menu?.onContextMenu}>
                            {selectionCell}
                            <TableCell style={{ width: '100%' }} className={classes.nameCell}>
                              <TableCellLayout truncate>
                                <div className={classes.rowContent}>
                                  {attachment ? (
                                    <FluentLink
                                      className={classes.nameLink}
                                      onClick={(event) => {
                                        preventSelect?.(event);
                                        handleOpenAttachment(attachment);
                                      }}
                                    >
                                      {attachment.fileName}
                                    </FluentLink>
                                  ) : (
                                    <Text className={`${classes.name} ${classes.missingName}`}>
                                      Unavailable attachment
                                    </Text>
                                  )}
                                </div>
                              </TableCellLayout>
                            </TableCell>
                            <TableCell style={{ width: '140px' }}>
                              {attachment ? <DateLabel short>{attachment.createdDate}</DateLabel> : '-'}
                            </TableCell>
                            <TableCell style={{ width: '80px' }}>
                              <div className={classes.creatorCell}>
                                <Avatar user={creator} size={28} tooltip={creator ? 'name' : 'none'} />
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '56px' }} onClick={preventSelect}>
                              <div className={classes.actions}>
                                <TableActionButtons alwaysVisible={alwaysVisible}>
                                  <Menu open={menu?.isOpen} onOpenChange={menu?.onOpenChange}>
                                    <MenuTrigger disableButtonEnhancement>
                                      <Button
                                        appearance="secondary"
                                        size="small"
                                        icon={<MoreHorizontalFilled />}
                                        aria-label="Attachment actions"
                                      />
                                    </MenuTrigger>
                                    <MenuPopover>
                                      <MenuList>
                                        <MenuItemLoading
                                          icon={<DeleteRegular />}
                                          disabled={!canManageAttachments || isBulkRemoving}
                                          loading={removeLoading}
                                          onClick={async () => {
                                            if (canManageAttachments && !isBulkRemoving) {
                                              await removeAttachment.mutateAsync({
                                                id: documentId,
                                                attachmentId: item.attachmentId,
                                              });
                                            }
                                          }}
                                        >
                                          Remove
                                        </MenuItemLoading>
                                        <MenuItemLoading
                                          icon={<ArrowDownloadRegular />}
                                          disabled={!attachment || isBulkRemoving}
                                          onClick={async () => {
                                            if (attachment && !isBulkRemoving) handleExportAttachment(attachment);
                                          }}
                                        >
                                          Export
                                        </MenuItemLoading>
                                      </MenuList>
                                    </MenuPopover>
                                  </Menu>
                                </TableActionButtons>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DocumentAttachmentsDialog;
